import { useEffect, useState } from 'react';
import { Shell } from '../components/Shell.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button, EmptyState, Switch } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { native, registerPhone, useNative, type NativeInfo } from '../lib/native.ts';
import { usePlaces } from '../lib/places.tsx';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';

/**
 * This phone (only in the ERP PRO app): alerts with the phone in a pocket for food ready at
 * the pass and guests calling, whose tables they cover, the screen kept on, and the server.
 */
export function Phone() {
  const info = useNative();
  return (
    <Shell>
      <div className="page phone-page">
        <div className="page-head">
          <div>
            <h1>{t('phone.title')}</h1>
            <p className="muted">{info ? t('phone.subtitle') : t('phone.subtitle_browser')}</p>
          </div>
        </div>
        {info ? <Settings info={info} /> : <NotInApp />}
      </div>
    </Shell>
  );
}

function NotInApp() {
  return (
    <EmptyState
      icon="phone"
      title={t('phone.browser_title')}
      body={t('phone.browser_body')}
      action={
        <a className="btn btn-primary" href="/get-app">
          <Icon name="download" size={18} />
          <span className="btn-label">{t('phone.get_app')}</span>
        </a>
      }
    />
  );
}

function Settings({ info }: { info: NativeInfo }) {
  const { can } = useSession();
  const { here, multi } = usePlaces();
  const { toast } = useUi();
  const covers = can('restaurant.view') || can('online.handle');
  const [scope, setScope] = useState(info.scope);
  useEffect(() => setScope(info.scope), [info.scope]);
  // The phone listens where it works now: its location follows the place chosen on this device.
  const location = multi && here ? here.id : null;
  useEffect(() => {
    if (info.alerts && (info.location || null) !== location) native.alerts(true, info.scope, location);
  }, [info.alerts, info.location, info.scope, location]);
  // Tell the server about the phone whenever its alert settings change (Manage → Phones).
  useEffect(() => {
    void registerPhone(info);
  }, [info.alerts, info.scope, info.location, info.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const turn = (on: boolean) => {
    native.alerts(on, scope, location);
    toast(on ? t('phone.alerts_on_toast') : t('phone.alerts_off_toast'));
  };
  const choose = (s: 'mine' | 'location') => {
    setScope(s);
    native.alerts(info.alerts, s, location);
  };

  const state: { tone: 'ok' | 'warn' | 'neutral'; icon: IconName; title: MessageKey; body: MessageKey } = !info.alerts
    ? { tone: 'neutral', icon: 'bell', title: 'phone.state_off', body: 'phone.state_off_body' }
    : !info.notifications
      ? { tone: 'warn', icon: 'alert', title: 'phone.state_blocked', body: 'phone.state_blocked_body' }
      : { tone: 'ok', icon: 'checkCircle', title: 'phone.state_on', body: info.scope === 'location' ? 'phone.state_on_all' : 'phone.state_on_mine' };

  return (
    <div className="phone-grid">
      <section className="card" aria-labelledby="phone-alerts">
        <header className="card-head">
          <div>
            <h2 id="phone-alerts">{t('phone.alerts_title')}</h2>
            <span className="muted">{t('phone.alerts_hint')}</span>
          </div>
          <Switch checked={info.alerts} onChange={turn} label={t('phone.alerts_switch')} disabled={!covers} />
        </header>
        {!covers ? (
          <p className="callout callout-info">
            <Icon name="alert" size={16} /> {t('phone.alerts_not_for_role')}
          </p>
        ) : (
          <>
            <div className={`phone-state phone-state-${state.tone}`} role="status">
              <Icon name={state.icon} size={22} />
              <div>
                <strong>{t(state.title)}</strong>
                <span>{t(state.body, { place: here?.name ?? '' })}</span>
              </div>
              {info.alerts && !info.notifications ? (
                <Button variant="primary" size="sm" onClick={() => native.allowNotifications()}>
                  {t('phone.allow')}
                </Button>
              ) : null}
            </div>
            <div className="team-modes" role="radiogroup" aria-label={t('phone.scope_title')}>
              {(['mine', 'location'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={scope === s}
                  className={`team-mode${scope === s ? ' active' : ''}`}
                  onClick={() => choose(s)}
                >
                  <Icon name={s === 'mine' ? 'users' : 'store'} size={22} />
                  <strong>{t(`phone.scope_${s}` as MessageKey)}</strong>
                  <span className="muted">{t(`phone.scope_${s}_hint` as MessageKey, { place: multi && here ? here.name : t('phone.here') })}</span>
                </button>
              ))}
            </div>
            <ul className="phone-what">
              <li>
                <Icon name="bell" size={18} /> {t('phone.what_ready')}
              </li>
              <li>
                <Icon name="users" size={18} /> {t('phone.what_call')}
              </li>
              <li>
                <Icon name="receipt" size={18} /> {t('phone.what_bill')}
              </li>
            </ul>
            <div className="phone-actions">
              <Button icon="bell" onClick={() => native.testAlert()}>
                {t('phone.test')}
              </Button>
              <Button variant="ghost" icon="settings" onClick={() => native.notificationSettings()}>
                {t('phone.sound_settings')}
              </Button>
            </div>
            {info.alerts && !info.battery_saver_free ? (
              <div className="callout callout-info phone-battery">
                <Icon name="alert" size={16} />
                <span>{t('phone.battery')}</span>
                <Button size="sm" variant="secondary" onClick={() => native.batterySettings()}>
                  {t('phone.battery_open')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="card" aria-labelledby="phone-screen">
        <header className="card-head">
          <div>
            <h2 id="phone-screen">{t('phone.awake_title')}</h2>
            <span className="muted">{t('phone.awake_hint')}</span>
          </div>
          <Switch checked={info.keep_awake} onChange={(on) => native.keepAwake(on)} label={t('phone.awake_switch')} />
        </header>
      </section>

      <section className="card" aria-labelledby="phone-about">
        <header className="card-head">
          <div>
            <h2 id="phone-about">{t('phone.about_title')}</h2>
          </div>
        </header>
        <dl className="phone-facts">
          <dt>{t('phone.server')}</dt>
          <dd className="mono">{info.server.replace(/^https?:\/\//, '')}</dd>
          <dt>{t('phone.model')}</dt>
          <dd>{t('phone.model_value', { model: info.model, os: info.os })}</dd>
          <dt>{t('phone.app_version')}</dt>
          <dd>{info.version}</dd>
        </dl>
        <div className="phone-actions">
          <Button variant="secondary" icon="transfer" onClick={() => native.changeServer()}>
            {t('phone.change_server')}
          </Button>
        </div>
      </section>
    </div>
  );
}
